import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TransferModel } from '../../../core/models/app.models';
import { TransferService } from '../../../core/services/transfer.service';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

interface TransferOption extends TransferModel {
  label: string;
}

/**
 * Výběr přestupu hledáním (hráč / klub) s bindingem na transferId (ControlValueAccessor).
 * Vzor podle app-club-select — lazy serverový filtr přes TransferService.getAll(search).
 * Nahrazuje syrové zadávání ID přestupu (viz dialog Nový úkol).
 */
@Component({
  selector: 'app-transfer-select',
  template: `
    <p-dropdown [options]="transfers"
                [(ngModel)]="value"
                [showClear]="showClear"
                [filter]="true"
                [lazy]="true"
                (onFilter)="onSearch($event)"
                [placeholder]="placeholder"
                [styleClass]="styleClass"
                [style]="{'width': '100%'}"
                appendTo="body"
                [loading]="loading"
                (onChange)="onChanged($event.value)"
                optionLabel="label"
                optionValue="id">

      <ng-template let-t pTemplate="item">
        <div class="flex flex-column py-1">
          <span class="font-bold">{{ playerName(t) }}</span>
          <small class="text-400">{{ t.fromClub?.name }} → {{ t.toClub?.name }}</small>
          <small class="text-500">{{ typeLabel(t) }} · {{ fmtDate(t.date) }}<span *ngIf="t.loanEndDate"> · do {{ fmtDate(t.loanEndDate) }}</span><span class="text-300" style="font-size: 0.72rem"> · {{ statusLabel(t) }}</span></small>
        </div>
      </ng-template>
    </p-dropdown>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TransferSelectComponent),
      multi: true
    }
  ]
})
export class TransferSelectComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = 'Hledejte přestup (hráč nebo klub)';
  @Input() showClear: boolean = true;
  @Input() styleClass: string = '';
  /** Když je zadáno, nabídka se omezí na přestupy tohoto hráče (řetězení přestupů). */
  @Input() playerId?: number;
  /** ID přestupu, které se z nabídky vynechá (typicky sám sebe — přestup nemůže navazovat na sebe). */
  @Input() excludeId?: number;

  transfers: TransferOption[] = [];
  loading = false;
  private searchSubject = new Subject<string>();

  _value: any;

  constructor(private transferService: TransferService) {}

  ngOnInit() {
    this.loadInitial();

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        this.loading = true;
        return this.transferService.getAll(1, 20, query && query.length >= 2 ? query : undefined,
          undefined, undefined, undefined, undefined, undefined, undefined, undefined, this.playerId);
      })
    ).subscribe(res => {
      this.transfers = this.mapOptions(res.data);
      this.loading = false;
      this.ensureSelectionInList();
    });
  }

  private loadInitial() {
    this.loading = true;
    this.transferService.getAll(1, 20, undefined,
      undefined, undefined, undefined, undefined, undefined, undefined, undefined, this.playerId).subscribe(res => {
      this.transfers = this.mapOptions(res.data);
      this.loading = false;
      this.ensureSelectionInList();
    });
  }

  /** Namapuje na options a vynechá vyloučený přestup (sám sebe). */
  private mapOptions(data: TransferModel[]): TransferOption[] {
    return data
      .filter(t => this.excludeId == null || t.id !== this.excludeId)
      .map(t => this.toOption(t));
  }

  playerName(t: TransferModel): string {
    return t.player ? `${t.player.lastName ?? ''} ${t.player.firstName ?? ''}`.trim() : `Hráč #${t.playerId}`;
  }

  /** Forma přestupu (1=Přestup, 2=Hostování, 3=Spolupráce, jinak Bude upřesněno). */
  typeLabel(t: TransferModel): string {
    return t.type === 1 ? 'Přestup' : t.type === 2 ? 'Hostování' : t.type === 3 ? 'Spolupráce' : 'Bude upřesněno';
  }

  /** Stav přestupu (1–6) — jen decentní doplněk v nabídce. */
  statusLabel(t: TransferModel): string {
    switch (t.status) {
      case 1: return 'K vyřešení';
      case 2: return 'Čeká na protistranu';
      case 3: return 'Čeká na podpisy';
      case 4: return 'Potvrzení FAČR';
      case 5: return 'Vyřešeno';
      case 6: return 'Stornováno';
      default: return '';
    }
  }

  /** Datum ve tvaru dd.MM.yyyy; prázdné pro chybějící/nevalidní hodnotu. */
  fmtDate(d?: string | Date | null): string {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(date.getDate())}.${p(date.getMonth() + 1)}.${date.getFullYear()}`;
  }

  private toOption(t: TransferModel): TransferOption {
    const clubs = `${t.fromClub?.name ?? '?'} → ${t.toClub?.name ?? '?'}`;
    const realiz = this.fmtDate(t.date);
    const meta = realiz ? `${this.typeLabel(t)} ${realiz}` : this.typeLabel(t);
    return { ...t, label: `${this.playerName(t)} · ${meta} · ${clubs}` };
  }

  onSearch(event: any) {
    this.searchSubject.next(event.filter);
  }

  /** Zajistí, že aktuálně vybraný přestup je v seznamu (jinak by ho p-dropdown neuměl zobrazit). */
  private ensureSelectionInList() {
    if (this._value && !this.transfers.find(t => t.id === this._value)) {
      this.transferService.getById(this._value).subscribe(t => {
        if (t && !this.transfers.find(x => x.id === t.id)) {
          this.transfers = [this.toOption(t), ...this.transfers];
        }
      });
    }
  }

  get value() { return this._value; }
  set value(v: any) {
    if (v !== this._value) {
      this._value = v;
      this.onChange(v);
    }
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this._value = value;
    if (value) this.ensureSelectionInList();
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  onChanged(val: any) {
    this.value = val;
  }
}
